import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { DesignContext } from '../../../contexts/DesignContext'
import { Form, Row, Col, Image } from 'react-bootstrap'
import Webcam from 'react-webcam'
import CustomButton from './Button'
import ReactPlayer from 'react-player'
import Timer from './Timer'
import Label from './Label'
import objectPath from 'object-path'

const CustomVideoWebcam = (props) => {
  const { uischema, config } = props

  const {
    isDynamic = false,
    dynamicName = '',
    dynamicIndex = 0,
    dynamicData = {},
    onUpdateFormData = () => {},
    isArray,
    arrayName,
  } = config

  const fieldKey = isArray
    ? `@${arrayName}.${uischema.key}`
    : isDynamic
    ? `${dynamicName}.${dynamicIndex}.${uischema.key}`
    : uischema.key
  const fieldName = isArray
    ? `@${arrayName}.${uischema.name}`
    : isDynamic
    ? `${dynamicName}.${dynamicIndex}.${uischema.name}`
    : uischema.name

  const designContext = useContext(DesignContext)
  const { jsonForm, setFormData, findUischemaIndex, elementAction } =
    designContext
  const isLocked = objectPath.get(elementAction, ['isLockedEdit'])
  let hasEnable = objectPath.has(elementAction, ['isEnable', uischema.key])
  let isEnable = objectPath.get(elementAction, ['isEnable', uischema.key])
  let isReadOnly = useMemo(() => {
    if (isLocked) return isLocked
    if (hasEnable) {
      return isEnable === false ? true : false
    }
    return uischema.readonly
  }, [isEnable, uischema.readonly])

  const [inModal, setInModal] = useState(null)
  const [modal, setModal] = useState({})
  const webcamRef = useRef(null)
  const mediaRecorderRef = useRef(null)
  const [capture, setCapture] = useState('')
  const [capturing, setCapturing] = useState(false)
  const [recordedChunks, setRecordedChunks] = useState([])
  const [isSave, setIsSave] = useState(false)

  const undoCapture = () => {
    setCapture()
    setIsSave(false)
  }

  const saveCapture = async () => {
    let file = await createFile()
    if (!file) return
    let newFormData = jsonForm.formData
    newFormData[uischema.key] = [file]
    setFormData(newFormData)
    setIsSave(true)
  }

  const createFile = useCallback(() => {
    if (recordedChunks.length) {
      const blob = new Blob(recordedChunks, {
        type: 'video/mp4',
      })
      const url = URL.createObjectURL(blob)
      setCapture(url)
      setRecordedChunks([])
      const extension = blob.type.split('/').pop()
      let video = new File([blob], `video.${extension}`, {
        type: blob.type,
      })
      video = Object.assign(video, {
        path: `video.${extension}`,
      })
      return video
    }
    return {}
  }, [recordedChunks])

  const startVideoCapture = useCallback(() => {
    setRecordedChunks([])
    if (webcamRef.current.stream) {
      setCapturing(true)
      mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream)
      mediaRecorderRef.current.addEventListener(
        'dataavailable',
        handleDataAvailable
      )
      mediaRecorderRef.current.start()
    }
  }, [webcamRef, setCapturing, mediaRecorderRef])

  const handleDataAvailable = useCallback(
    ({ data }) => {
      if (data.size > 0) {
        setRecordedChunks((prev) => prev.concat(data))
      }
    },
    [setRecordedChunks]
  )

  const stopVideoCapture = useCallback(async () => {
    mediaRecorderRef.current.stop()
    setCapturing(false)
  }, [mediaRecorderRef, webcamRef, setCapturing])

  const checkModal = (buttonKey) => {
    let schema = jsonForm.schema
    let mapTag = schema.mapTag
    let mapKey = schema.mapKey[buttonKey].split('.')
    mapKey.pop()
    mapKey.shift()
    let modalKey = mapKey.filter((key) => mapTag[key] === 'modal')
    if (modalKey.length > 0) {
      let lastModalKey = modalKey.pop()
      let elements = jsonForm.uischema.elements
      let modalMapKey = jsonForm.schema.mapKey[lastModalKey]
      let indices = findUischemaIndex(elements, modalMapKey)
      let newElements = indices.reduce((o, i) => o.elements[i], { elements })
      setModal(newElements)
      setInModal(modalKey)
    } else {
      setModal({})
      setInModal(null)
    }
  }

  useEffect(() => {
    if (uischema.action) {
      checkModal(uischema.key)
    }
  }, [])

  return (
    <Form.Group className="mb-0" controlId={uischema.controlId}>
      <Row className="w-100 m-0">
        <Label {...props} uischema={uischema} />
        <Col
          sm={uischema.elementWidth}
          className="d-flex justify-content-center"
        >
          {capture ? (
            <ReactPlayer
              id={`${fieldKey}-player`}
              style={{
                width: uischema.width,
                height: uischema.height,
              }}
              url={capture}
              controls={true}
              playsinline={true}
            />
          ) : (
            <div
              className="d-flex"
              style={{
                width: uischema.width,
                height: uischema.height,
              }}
            >
              {modal.isOpen || !inModal ? (
                <Webcam
                  id={`${fieldKey}-webcam`}
                  width={uischema.width}
                  audio={false}
                  ref={webcamRef}
                  videoConstraints={{ facingMode: 'user' }}
                />
              ) : (
                <div
                  className="d-flex bg-light"
                  style={{
                    width: '100%',
                    height: uischema.height,
                  }}
                ></div>
              )}
            </div>
          )}
        </Col>
      </Row>
      {!isReadOnly && (
        <Row className="p-1">
          <Col sm={uischema.button.undo.layoutWidth} className="mt-2">
            <CustomButton
              fieldKey={`${fieldKey}-undo`}
              fieldName={`${fieldName}-undo`}
              uischema={uischema.button.undo}
              onCustomClick={undoCapture}
              customClick={true}
            />
          </Col>
          {capturing ? (
            <Col sm={uischema.button.end.layoutWidth} className="mt-2">
              <CustomButton
                fieldKey={`${fieldKey}-end`}
                fieldName={`${fieldName}-end`}
                uischema={uischema.button.end}
                onCustomClick={stopVideoCapture}
                customClick={true}
                disabled={isSave}
              />
            </Col>
          ) : (
            <Col sm={uischema.button.start.layoutWidth} className="mt-2">
              <CustomButton
                fieldKey={`${fieldKey}-start`}
                fieldName={`${fieldName}-start`}
                uischema={uischema.button.start}
                onCustomClick={startVideoCapture}
                customClick={true}
                disabled={isSave}
              />
            </Col>
          )}
          {!isSave && (
            <Col sm="auto" className="mt-2 d-flex align-items-center">
              <Timer
                id={`${fieldKey}-timer`}
                isStart={capturing}
                isReset={!isSave}
              />
            </Col>
          )}

          <Col sm={uischema.button.capture.layoutWidth} className="mt-2">
            <CustomButton
              fieldKey={`${fieldKey}-capture`}
              fieldName={`${fieldName}-capture`}
              uischema={uischema.button.capture}
              onCustomClick={saveCapture}
              customClick={true}
              disabled={isSave || recordedChunks.length === 0}
            />
          </Col>
        </Row>
      )}
    </Form.Group>
  )
}
export default CustomVideoWebcam
